import React from 'react'
import { View, CardViewProps } from './View'
import { concatStyles } from 'utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight'
import { faUserEdit } from '@fortawesome/free-solid-svg-icons/faUserEdit'
import { SubHeading } from '../Content/SubHeading'

export interface ProfileCompletionAction {
  /** Label for the action item */
  label: string
  /** Path to navigate to when clicked */
  path: string
  /** Whether this action has been completed */
  completed: boolean
}

export interface ProfileCompletionCardProps
  extends Omit<CardViewProps, 'onClick'> {
  /** An array of actions to display */
  actions: ProfileCompletionAction[]
  /** Percentage of profile completion (0-100) */
  percentComplete: number
  /** Handler for when an action is clicked */
  onActionClick?: (path: string) => void
}

const CARD_STYLES = 'bg-white p-5 flex flex-col'
const PROGRESS_CONTAINER_STYLES = 'w-full h-2 bg-gray-100 rounded-full mb-6'
const PROGRESS_INDICATOR_STYLES =
  'h-2 bg-actionable rounded-full transition-all duration-500'
const HEADER_STYLES = 'flex items-center mb-4'
const ICON_STYLES = 'text-primary mr-2'
const TITLE_STYLES = 'text-lg font-semibold'
const PERCENT_STYLES = 'ml-auto font-semibold'
const ACTION_LIST_STYLES = 'flex flex-col space-y-2'
const PENDING_SECTION_STYLES = 'mb-4'
const COMPLETED_SECTION_STYLES = 'pt-2 border-t border-gray-100'
const COMPLETED_HEADER_STYLES =
  'text-xs uppercase text-gray-500 mb-2 font-semibold'
const ANIMATION_PULSE = 'animate-pulse'

const ActionItem: React.FC<{
  action: ProfileCompletionAction
  onActionClick?: (path: string) => void
  isNew?: boolean
}> = ({ action, onActionClick, isNew }) => {
  const actionStyles = concatStyles([
    'flex items-center p-2 rounded hover:bg-gray-50 transition-colors duration-200',
    action.completed ? 'text-gray-500' : 'text-actionable',
    isNew && !action.completed ? ANIMATION_PULSE : '',
  ])

  const handleClick = () => {
    if (onActionClick) {
      onActionClick(action.path)
    }
  }

  return (
    <div
      className={actionStyles}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick()
        }
      }}
    >
      <div className="flex-1">{action.label}</div>
      <div className="ml-2">
        {action.completed ? (
          <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
        ) : (
          <FontAwesomeIcon icon={faChevronRight} />
        )}
      </div>
    </div>
  )
}

export const ProfileCompletion: React.FC<ProfileCompletionCardProps> = ({
  actions,
  percentComplete,
  onActionClick,
  className,
  ...view
}) => {
  // Separate actions into pending and completed
  const pendingActions = actions.filter((action) => !action.completed)
  const completedActions = actions.filter((action) => action.completed)

  // Ensure percent complete is within valid range
  const percent = Math.min(100, Math.max(0, percentComplete))

  // Determine the most recent added item for animation
  const hasNewAction = pendingActions.length > 0

  return (
    <View {...view} className={concatStyles([CARD_STYLES, className])} shadow>
      <div className={HEADER_STYLES}>
        <FontAwesomeIcon icon={faUserEdit} size="lg" className={ICON_STYLES} />
        <h3 className={TITLE_STYLES}>Profile Completion</h3>
        <span className={PERCENT_STYLES}>{percent}%</span>
      </div>

      <div className={PROGRESS_CONTAINER_STYLES}>
        <div
          className={PROGRESS_INDICATOR_STYLES}
          style={{ width: `${percent}%` }}
        />
      </div>

      {pendingActions.length > 0 && (
        <div className={PENDING_SECTION_STYLES}>
          <SubHeading>Actions Required</SubHeading>
          <div className={ACTION_LIST_STYLES}>
            {pendingActions.map((action, index) => (
              <ActionItem
                key={`pending-${index}`}
                action={action}
                onActionClick={onActionClick}
                isNew={index === 0}
              />
            ))}
          </div>
        </div>
      )}

      {completedActions.length > 0 && (
        <div className={COMPLETED_SECTION_STYLES}>
          <div className={COMPLETED_HEADER_STYLES}>Completed</div>
          <div className={ACTION_LIST_STYLES}>
            {completedActions.map((action, index) => (
              <ActionItem
                key={`completed-${index}`}
                action={action}
                onActionClick={onActionClick}
              />
            ))}
          </div>
        </div>
      )}
    </View>
  )
}

ProfileCompletion.displayName = 'Card.ProfileCompletion'
